import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { AppService } from '@Core/services/proprietary/app.service';
import { cloneDeep } from 'src/ep-utils';
import { StateHelperService } from '../../../services/proprietary/state-helper.service';
import { AddArticleInterestPointer, RemoveArticleInterestPointer, UpdateArticle } from './article.actions';
import { ContractObjectDefaults } from './contract-object.defaults';
import { AddContractInterestPointer, RemoveContractInterestPointer } from './contract.actions';
import { AddThirdPartyEntity, DeleteThirdPartyEntity } from './entity.actions';
import {
    AddEntityInterestLink,
    AddInterest,
    CloneInterest,
    DeleteInterest,
    RemoveEntityInterestLink,
    RestoreInterest,
    UpdateInterest
} from './interest.actions';

export interface InterestStateModel {
    interest: any;
    currentID: string;
}

@State<InterestStateModel>({
    name: 'interestState',
    defaults: {
        interest: {},
        currentID: null
    }
})
@Injectable()
export class InterestState {
    constructor(private helper: StateHelperService, private app: AppService) { }

    //******* SELECTORS *********
    @Selector()
    static contractInterests(state: InterestStateModel) {
        let result: any = [];
        for (var i in state.interest) {
            if (state.interest[i].Ref_Type == 'Contract') {
                result.push(state.interest[i]);
            }
        }
        return result;
    }

    @Selector()
    static contractInterestCount(state: InterestStateModel) {
        let count = 0;
        for (var i in state.interest) {
            if (state.interest[i].Ref_Type == 'Contract') {
                count++;
            }
        }
        return count;
    }

    @Selector()
    static interestByArticleID(state: InterestStateModel) {
        return (articleID: string) => {
            let result = [];
            if (articleID) {
                for (var i in state.interest) {
                    if (state.interest[i].Ref_Type == 'Article' && state.interest[i].Ref_Article_ID == articleID) {
                        result.push(state.interest[i]);
                    }
                }
            }
            return result;
        };
    }

    @Selector()
    static interestByID(state: InterestStateModel) {
        return (interestID: string) => {
            let result = {};
            if (interestID) {
                result = state.interest[interestID];
            }
            return result;
        };
    }

    //******  ACTIONS  ******

    @Action(UpdateInterest)
    updateInterest(sc: StateContext<InterestStateModel>, action: UpdateInterest) {
        const state = sc.getState();
        let interestID = action.interest.Interest_ID;
        let updateInterest: any = { ...action.interest };
        let currentInterest = cloneDeep(state.interest[interestID]);

        updateInterest = { ...currentInterest, ...updateInterest }; //merge the data from current and the updated data passed in.

        this.helper.setItemChanged(updateInterest, state.interest[interestID].Edit_Status);

        sc.patchState({
            interest: {
                ...state.interest,
                [interestID]: updateInterest
            }
        });

        if (updateInterest.Ref_Type == 'Article') {
            sc.dispatch(new UpdateArticle({ Article_ID: updateInterest.Ref_Article_ID }));
        }
    }

    @Action(DeleteInterest)
    deleteInterest(sc: StateContext<InterestStateModel>, action: DeleteInterest) {
        const state = sc.getState();
        let interest = { ...state.interest };
        let deletedInterest = { ...state.interest[action.interestID] };
        this.helper.setItemDeleted(interest, action.interestID);
        sc.patchState({ interest: interest });
        if (action.interestID < 0) {
            if (deletedInterest.Ref_Type == 'Contract') {
                sc.dispatch(new RemoveContractInterestPointer(action.interestID.toString()));
            } else {
                sc.dispatch(new RemoveArticleInterestPointer(deletedInterest.Ref_Article_ID, action.interestID.toString()));
            }

            sc.dispatch(new DeleteThirdPartyEntity(deletedInterest.Interest_Name));
        }
        if (deletedInterest.Ref_Type == 'Article') {
            sc.dispatch(new UpdateArticle({ Article_ID: deletedInterest.Ref_Article_ID }));
        }
    }

    @Action(RestoreInterest)
    restoreInterest(sc: StateContext<InterestStateModel>, action: RestoreInterest) {
        const state = sc.getState();
        let interest = { ...state.interest };
        let restoredInterest = interest[action.interestID];
        this.helper.setItemRestored(interest, action.interestID);
        sc.patchState({ interest: interest });

        if (restoredInterest.Ref_Type == 'Article') {
            sc.dispatch([new UpdateArticle({ Article_ID: restoredInterest.Ref_Article_ID })]);
        }
    }

    @Action(CloneInterest)
    cloneInterest(sc: StateContext<InterestStateModel>, action: CloneInterest) {
        const state = sc.getState();
        let srcID = null;
        srcID = action.interestID;
        if (!srcID) {
            throw 'Source interest not found in store.';
        }

        let newID = this.helper.getTempID();
        //Match C1, added interests always set to 0. Only duplicating will set Num highest number +1
        let newInterestNum = this.helper.getNextItemNumber(state.interest, 'Interest_Num');
        let newInterest = cloneDeep(state.interest[srcID]);
        this.helper.setItemAdded(newInterest);
        newInterest.Interest_ID = newID;
        newInterest.Interest_Num = newInterestNum;

        sc.patchState({
            currentID: newID,
            interest: { ...state.interest, [newID]: newInterest }
        });

        if (newInterest.Ref_Type == 'Contract') {
            sc.dispatch(new AddContractInterestPointer(newID));
        } else {
            sc.dispatch([
                new AddArticleInterestPointer(newInterest.Ref_Article_ID, newID),
                new UpdateArticle({ Article_ID: newInterest.Ref_Article_ID })
            ]);
        }
    }

    @Action(AddInterest)
    addInterest(sc: StateContext<InterestStateModel>, action: AddInterest) {
        const state = sc.getState();
        let newID = this.helper.getTempID();
        let newInterest = this.helper.createDefaultItem(ContractObjectDefaults, 'Contract_Interest', action.interest);
        let thirdParty = this.helper.createDefaultItem(ContractObjectDefaults, 'Interest_Name', action.thirdParty);
        newInterest.Interest_Num = 0; //Match C1, added interests always set to 0. Only duplicating will set Num highest number +1

        if (action.ref && action.ref.Ref_Type && (action.ref.Ref_Type == 'Contract' || action.ref.Ref_Type == 'Article')) {
            if (action.ref.Ref_Type == 'Article' && action.ref.Ref_Article_ID) {
                newInterest.Ref_Type = 'Article';
                newInterest.Ref_Article_ID = action.ref.Ref_Article_ID;
            } else {
                newInterest.Ref_Type = 'Contract';
            }
        } else {
            newInterest.Ref_Type = 'Contract';
        }

        this.helper.setItemAdded(newInterest);
        newInterest.Interest_ID = newID;

        let entityFound: boolean = false;
        for (var i in state.interest) {
            if (state.interest[i].Entity_ID == thirdParty.Entity_ID) {
                entityFound = true;
                break;
            }
        }

        if (!entityFound) {
            sc.patchState({
                currentID: newID,
                interest: { ...state.interest, [newID]: newInterest }
            });

            sc.dispatch(new AddThirdPartyEntity(thirdParty, newInterest.Interest_ID));

            if (newInterest.Ref_Type == 'Contract') {
                sc.dispatch(new AddContractInterestPointer(newID));
            } else {
                sc.dispatch([
                    new AddArticleInterestPointer(newInterest.Ref_Article_ID, newID),
                    new UpdateArticle({ Article_ID: newInterest.Ref_Article_ID })
                ]);
            }
        }
    }

    @Action(AddEntityInterestLink)
    addEntityInterestLink(sc: StateContext<InterestStateModel>, action: AddEntityInterestLink) {
        const state = sc.getState();
        let entityID = action.entityID;
        let interestID = action.interestID;
        let updateInterest: any = cloneDeep(state.interest[interestID]);
        this.helper.setItemChanged(updateInterest, updateInterest.Edit_Status);

        updateInterest.Interest_Name = entityID;

        sc.patchState({
            interest: {
                ...state.interest,
                [interestID]: { ...state.interest[interestID], ...updateInterest }
            }
        });
    }

    @Action(RemoveEntityInterestLink)
    removeEntityInterestLink(sc: StateContext<InterestStateModel>, action: RemoveEntityInterestLink) {
        const state = sc.getState();
        let entityID = action.entityID;
        let interestID = action.interestID;
        let updateInterest: any = cloneDeep(state.interest[interestID]);
        this.helper.setItemChanged(updateInterest, updateInterest.Edit_Status);

        if (updateInterest.Interest_Name == entityID) {
            updateInterest.Interest_Name = '';
        }

        sc.patchState({
            interest: {
                ...state.interest,
                [interestID]: { ...state.interest[interestID], ...updateInterest }
            }
        });
    }
}
